/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState } from "react";
import { getOneTradeOrganization } from "../../lib/sysNavigation/SysNavigation";
import { useTradeOrganizationContext } from "../../context/TradeOrganizationContext";
import {
  TextArea,
  InputField,
  MyMessage,
  AddPageShell,
  AddSection,
} from "../components/Component";
import { ImageKitUpload } from "../../ui/ImageKitLibrary";
import { FOLDERS } from "../doseeData/utils/Utils";
import { defaultImage } from "../../lib/libraries/imageKit/ImageKit";
import { AiFillBuild } from "react-icons/ai";
import {
  FiFlag,
  FiTarget,
  FiImage,
  FiAlignLeft,
} from "react-icons/fi";

const Edit = () => {
  const { updateTradeOrganizationState, updateTradeOrganization } =
    useTradeOrganizationContext();

  const tradeOrganization = getOneTradeOrganization();

  const [error, setError] = useState(false);
  const [message, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const trade_organization_id = tradeOrganization?.trade_organization_id;

  const [name, setName] = useState(tradeOrganization?.name);
  const [acronym, setAcronym] = useState(tradeOrganization?.acronym);
  const [headquarters, setHeadquarters] = useState(
    tradeOrganization?.headquarters
  );
  const [objectives, setObjectives] = useState(tradeOrganization?.objectives);
  const [debuted_on, setDebutedOn] = useState(tradeOrganization?.debuted_on);
  const [mission, setMission] = useState(tradeOrganization?.mission);
  const [vision, setVision] = useState(tradeOrganization?.vision);
  const [description, setDescription] = useState(
    tradeOrganization?.description
  );

  const [thumbnail, setThumbnail] = useState(
    tradeOrganization?.thumbnail || defaultImage
  );
  const [thumbnailId, setThumbnailId] = useState(
    tradeOrganization?.image_cdn_id || "..."
  );
  const [website, setWebsite] = useState(tradeOrganization?.website);

  const tradeOrganizationRoutes = "/trade/org";

  const cleanFields = () => {
    setLoading(false);
    setSuccess(true);
  };

  const responses = (isError: any, msg: any) => {
    setError(isError);
    setSuccess(!isError);
    setLoading(false);
    setErrorMessage(msg);
  };

  const updateOneTradeOrganizationHandler = async (e: any) => {
    e.preventDefault();
    setLoading(true);
    if (!thumbnail || !thumbnailId) {
      setLoading(false);
      responses(true, "Image Required");
    } else {
      await sendDBRequest();
    }
  };

  const sendDBRequest = async () => {
    const data = {
      name,
      acronym,
      headquarters,
      chief_officer: "...",
      objectives,
      debuted_on,
      mission,
      vision,
      description,
      thumbnail,
      image_cdn_id: thumbnailId,
      website,
    };

    const { status } = await updateTradeOrganization(
      trade_organization_id,
      data
    );
    if (status === 200) {
      updateTradeOrganizationState({ trade_organization_id, ...data });
      cleanFields();
      responses(false, message);
    } else {
      setLoading(false);
      responses(true, message);
    }
  };

  return (
    <AddPageShell
      backRoute={tradeOrganizationRoutes}
      eyebrow="Geography • Trade Organisations"
      title="Edit Trade Organisation"
      subtitle="Updated a recognised body's identity, purpose and official logo."
      icon={<AiFillBuild className="h-5 w-5" />}
      loading={loading}
      submitLabel="Save Changes"
      onSubmit={updateOneTradeOrganizationHandler}
      onSubmitClick={() => {
        setError(false);
        setSuccess(false);
        setErrorMessage(null);
      }}
      message={
        <MyMessage
          error={error}
          message={message}
          success={success}
          onExpired={() => {
            setError(false);
            setSuccess(false);
          }}
          onClick={() => {
            setError(false);
            setSuccess(false);
            setErrorMessage("");
          }}
        />
      }
    >
      <AddSection
        title="Organisation Logo"
        hint="PNG or JPG. Use a clear, official logo where possible."
        icon={<FiImage className="h-4 w-4" />}
      >
        <ImageKitUpload
          thumbnail={thumbnail}
          thumbnailId={thumbnailId}
          setThumbnail={setThumbnail}
          setThumbnailId={setThumbnailId}
          folder={FOLDERS.TRADE_ORGANIZATIONS}
        />
      </AddSection>

      <AddSection
        title="Identity"
        hint="Official name, acronym, website and headquarters."
        icon={<FiFlag className="h-4 w-4" />}
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          <InputField
            props={{
              id: "title",
              title: "Name",
              type: "text",
              required: true,
              value: name ? name : "",
              onChange: (e: any) => {
                setName(e.target.value);
                setError(null);
                setSuccess(null);
              },
            }}
          />
          <InputField
            props={{
              id: "acronym",
              title: "Acronym",
              type: "text",
              value: acronym ? acronym : "",
              onChange: (e: any) => {
                setAcronym(e.target.value);
                setError(null);
                setSuccess(null);
              },
            }}
          />
          <InputField
            props={{
              id: "website",
              title: "Website",
              type: "text",
              value: website ? website : "",
              onChange: (e: any) => {
                setWebsite(e.target.value);
                setError(null);
                setSuccess(null);
              },
            }}
          />
          <InputField
            props={{
              id: "headquarters",
              title: "Headquarters",
              type: "text",
              value: headquarters ? headquarters : "",
              onChange: (e: any) => {
                setHeadquarters(e.target.value);
                setError(null);
                setSuccess(null);
              },
            }}
          />
          <InputField
            props={{
              id: "debuted_on",
              title: "Debuted On",
              type: "text",
              value: debuted_on ? debuted_on : "",
              onChange: (e: any) => {
                setDebutedOn(e.target.value);
                setError(null);
                setSuccess(null);
              },
            }}
          />
        </div>
      </AddSection>

      <AddSection
        title="Purpose & Narrative"
        hint="Objectives, mission and vision."
        icon={<FiTarget className="h-4 w-4" />}
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <TextArea
            props={{
              id: "objectives",
              title: "Objectives",
              type: "text",
              value: objectives ? objectives : "",
              onChange: (e: any) => {
                setObjectives(e.target.value);
                setError(null);
                setSuccess(null);
              },
            }}
          />
          <TextArea
            props={{
              id: "mission",
              title: "Mission",
              type: "text",
              value: mission ? mission : "",
              onChange: (e: any) => {
                setMission(e.target.value);
                setError(null);
                setSuccess(null);
              },
            }}
          />
          <TextArea
            props={{
              id: "vision",
              title: "Vision",
              type: "text",
              value: vision ? vision : "",
              onChange: (e: any) => {
                setVision(e.target.value);
                setError(null);
                setSuccess(null);
              },
            }}
          />
        </div>
      </AddSection>

      <AddSection
        title="Description"
        hint="A concise summary used across listings."
        icon={<FiAlignLeft className="h-4 w-4" />}
      >
        <TextArea
          props={{
            id: "description",
            title: "Description",
            type: "text",
            required: true,
            value: description ? description : "",
            onChange: (e: any) => {
              setDescription(e.target.value);
              setError(null);
              setSuccess(null);
            },
          }}
        />
      </AddSection>
    </AddPageShell>
  );
};

export default Edit;
